import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { useRouterActions } from 'react-resource-router';
import type { UpdateSearchAction, ShowSearchAction, ResetSearchAction } from 'state/actions/search';
import { ResetSearchOnUnMount } from 'view/search-modal/reset-search-on-unmount';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { BackButton } from '@atlassian/help-center-common-component/back-button';
import messages from './messages';
import {
    SearchBarContainer,
    HiddenLabel,
    InputBackground,
    SearchTextField,
    Icon,
    SearchFieldContainer,
    BackButtonContainer,
} from './styled';

export interface Props {
    term: string;
    onChange: ((term: string) => UpdateSearchAction) | ((term: string) => void);
    onClose: () => ShowSearchAction;
    resetSearch: () => ResetSearchAction;
    createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const SearchBar = ({ term, onChange, resetSearch, createAnalyticsEvent }: Props) => {
    const { formatMessage } = useIntl();
    const onSearchTermChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);

            if (createAnalyticsEvent) {
                createAnalyticsEvent({
                    analyticsType: 'UI',
                    action: 'changed',
                    actionSubject: 'searchTermValue',
                    actionSubjectId: 'searchTerm',
                }).fire();
            }
        },
        [createAnalyticsEvent, onChange]
    );

    useEffect(() => {
        if (createAnalyticsEvent) {
            createAnalyticsEvent({
                analyticsType: 'UI',
                action: 'rendered',
                actionSubject: 'searchBarTextField',
                actionSubjectId: 'searchBar',
            }).fire();
        }
    }, [createAnalyticsEvent]);

    const { goBack } = useRouterActions();

    const onBackButtonClick = useCallback(() => {
        goBack() as unknown as void;
    }, [goBack]);

    return (
        <ResetSearchOnUnMount resetSearch={resetSearch}>
            <SearchBarContainer>
                <>
                    <HiddenLabel>{formatMessage(messages.inputPlaceholder)}</HiddenLabel>
                    <InputBackground term={term} />
                    <BackButtonContainer>
                        <BackButton onClick={onBackButtonClick} actionSubjectId="closeSearch" />
                    </BackButtonContainer>
                    <SearchFieldContainer>
                        <SearchTextField
                            value={term}
                            name={formatMessage(messages.inputLabel)}
                            aria-label={formatMessage(messages.inputPlaceholder)}
                            type="text"
                            autoComplete="off"
                            spellCheck={false}
                            onChange={onSearchTermChange}
                            placeholder={formatMessage(messages.inputPlaceholder)}
                            required
                            // Disabling existing violations, should be fixed when revisited.
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            elemBeforeInput={
                                <Icon>
                                    <SearchIcon
                                        label={formatMessage(messages.searchIconLabel)}
                                        aria-hidden="true"
                                        size="small"
                                        primaryColor={token('color.icon', colors.N500)}
                                    />
                                </Icon>
                            }
                        />
                    </SearchFieldContainer>
                </>
            </SearchBarContainer>
        </ResetSearchOnUnMount>
    );
};

export default withAnalyticsEvents()(SearchBar);
